import { getEnvVar } from "../config/RuntimeConfig";

const HELP_CACHE_KEY = "helpDocHtmlCache";

const readHelpCache = (): string | null => {
  try {
    const raw = sessionStorage.getItem(HELP_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.bodyHtml === "string" && parsed.bodyHtml.trim()) {
      return parsed.bodyHtml;
    }
  } catch {
    // Ignore cache errors.
  }
  return null;
};

const writeHelpCache = (bodyHtml: string): void => {
  try {
    sessionStorage.setItem(
      HELP_CACHE_KEY,
      JSON.stringify({ bodyHtml, storedAt: Date.now() }),
    );
  } catch {
    // Ignore cache errors.
  }
};

const writeHelpWindow = (helpWindow: Window, html: string): boolean => {
  try {
    helpWindow.document.open();
    helpWindow.document.write(html);
    helpWindow.document.close();
    return true;
  } catch {
    return false;
  }
};

const pushHelpUrl = (urls: string[], value?: string | null): void => {
  if (!value) {
    return;
  }
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return;
  }
  urls.push(trimmedValue);
};

const looksLikeAppShellHtml = (html: string): boolean => {
  const normalized = html.toLowerCase();
  return normalized.includes('<div id="root"></div>')
    || normalized.includes('<script src="./dist/main.bundle.js"></script>')
    || normalized.includes('<script src="./config/env.js"></script>')
    || normalized.includes('<title>form builder</title>')
    || normalized.includes('<title>on form</title>');
};

const buildHelpContentUrls = (): string[] => {
  const baseUrl = getEnvVar('PUBLIC_URL') || "";
  const normalizedBase = baseUrl.replace(/\/$/, "");
  const urls: string[] = [];
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  const appBasePath = pathParts.length > 0 ? `/${pathParts[0]}` : "";
  const isRootServed = !normalizedBase || normalizedBase === "/";

  if (!isRootServed) {
    pushHelpUrl(urls, `${normalizedBase}/public/HelpContent.html`);
    if (appBasePath && appBasePath !== normalizedBase) {
      pushHelpUrl(urls, `${appBasePath}/public/HelpContent.html`);
    }
  } else {
    pushHelpUrl(urls, "/HelpContent.html");
  }

  return Array.from(new Set(urls));
};

const buildHtmlPage = (bodyHtml: string, title = "Help") => {
  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${title}</title>
    <style>
      body { font-family: Arial, sans-serif; margin: 24px; line-height: 1.6; color: #222; }
      img { max-width: 100%; height: auto; }
      table { border-collapse: collapse; width: 100%; }
      table, th, td { border: 1px solid #ccc; }
      th, td { padding: 8px; text-align: left; }
      #help-content {
        visibility: hidden;
      }
      .help-tabs {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin: 0 0 16px 0;
        padding: 0;
        list-style: none;
      }
      .help-tab {
        background: #f2f2f2;
        border: 1px solid #ccc;
        border-radius: 16px;
        padding: 6px 12px;
        cursor: pointer;
        font-size: 14px;
      }
      .help-tab[aria-selected="true"] {
        background: #0066cc;
        color: #fff;
        border-color: #0066cc;
      }
      .help-section {
        display: none;
      }
      .help-section.active {
        display: block;
      }
      .help-section-toc {
        margin: 0 0 16px 0;
        padding: 0;
        list-style: none;
      }
      .help-section-toc li {
        margin: 4px 0;
      }
      .help-section-toc a {
        color: #0066cc;
        text-decoration: none;
      }
      .help-section-toc a:hover {
        text-decoration: underline;
      }
      .help-top-button {
        position: fixed;
        right: 20px;
        bottom: 20px;
        background: #0066cc;
        color: #fff;
        border: none;
        border-radius: 20px;
        padding: 8px 14px;
        cursor: pointer;
        font-size: 14px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
      }
      .help-top-button:focus {
        outline: 2px solid #000;
        outline-offset: 2px;
      }
    </style>
  </head>
  <body>
    <div id="help-content">${bodyHtml}</div>
    <button class="help-top-button" onclick="window.scrollTo({ top: 0, behavior: 'smooth' })">Top</button>
    <script>
      (function () {
        const container = document.getElementById("help-content");
        if (!container) return;

        const isStandaloneTocParagraph = (paragraph) => {
          const link = paragraph.querySelector('a[href^="#_Toc"]');
          if (!link) return false;
          const meaningfulNodes = Array.from(paragraph.childNodes).filter((node) => {
            return !(node.nodeType === Node.TEXT_NODE && !node.textContent.trim());
          });
          return meaningfulNodes.length === 1 && meaningfulNodes[0] === link;
        };

        const tocParagraphs = Array.from(container.querySelectorAll("p")).filter(isStandaloneTocParagraph);
        tocParagraphs.forEach((paragraph) => paragraph.remove());

        const labelParagraphs = Array.from(container.querySelectorAll("p")).filter((paragraph) => {
          const text = (paragraph.textContent || "").replace(/\s+/g, " ").trim().toLowerCase();
          return text === "contents" || text === "table of contents";
        });
        labelParagraphs.forEach((paragraph) => paragraph.remove());

        const tabs = document.createElement("ul");
        tabs.className = "help-tabs";

        const slugify = (text) => {
          return (text || "section")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "");
        };

        const headings = Array.from(container.querySelectorAll("h1"));
        if (!headings.length) {
          container.style.visibility = "visible";
          return;
        }

        const sectionsWithHeadings = [];
        const nodes = Array.from(container.childNodes);
        let currentSection = null;
        nodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() === "h1") {
            currentSection = document.createElement("section");
            currentSection.className = "help-section";
            sectionsWithHeadings.push(currentSection);
            currentSection.appendChild(node);
            return;
          }
          if (currentSection) {
            currentSection.appendChild(node);
          }
        });

        sectionsWithHeadings.forEach((section, index) => {
          section.setAttribute("data-index", String(index));
          const heading = section.querySelector("h1");

          const toc = document.createElement("ul");
          toc.className = "help-section-toc";
          const subHeadings = Array.from(section.querySelectorAll("h2, h3"));
          subHeadings.forEach((subHeading, subIndex) => {
            const text = subHeading.textContent ? subHeading.textContent.trim() : "";
            if (!text) return;
            if (!subHeading.id) {
              const headingText = heading && heading.textContent ? heading.textContent : "";
              subHeading.id = slugify(headingText) + "-" + slugify(text) + "-" + (subIndex + 1);
            }
            const item = document.createElement("li");
            const link = document.createElement("a");
            link.href = "#" + subHeading.id;
            link.textContent = text;
            item.appendChild(link);
            toc.appendChild(item);
          });
          if (toc.children.length) {
            section.insertBefore(toc, section.firstChild);
          }

          const tab = document.createElement("li");
          tab.className = "help-tab";
          tab.setAttribute("role", "tab");
          tab.setAttribute("tabindex", index === 0 ? "0" : "-1");
          tab.setAttribute("aria-selected", index === 0 ? "true" : "false");
          tab.textContent = heading?.textContent ? heading.textContent.trim() : "Section " + (index + 1);

          tab.addEventListener("click", () => {
            sectionsWithHeadings.forEach((sec, i) => {
              const isActive = i === index;
              sec.classList.toggle("active", isActive);
              const t = tabs.children[i];
              if (t) {
                t.setAttribute("aria-selected", isActive ? "true" : "false");
                t.setAttribute("tabindex", isActive ? "0" : "-1");
              }
            });
            window.scrollTo({ top: 0, behavior: "smooth" });
          });

          tabs.appendChild(tab);
        });

        container.innerHTML = "";
        container.appendChild(tabs);
        sectionsWithHeadings.forEach((section) => container.appendChild(section));
        if (sectionsWithHeadings[0]) {
          sectionsWithHeadings[0].classList.add("active");
        }
        container.style.visibility = "visible";
      })();
    </script>
  </body>
</html>`;
};

const buildErrorPage = (message: string, title = "Help") => {
  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${title}</title>
    <style>
      body { font-family: Arial, sans-serif; margin: 24px; line-height: 1.6; color: #222; }
      button { padding: 8px 14px; border-radius: 4px; border: 1px solid #222; background: #fff; cursor: pointer; }
    </style>
  </head>
  <body>
    <h1>Help is unavailable</h1>
    <p>${message}</p>
    <button type="button" onclick="window.location.reload()">Retry</button>
  </body>
</html>`;
};

const fetchWithTimeout = async (url: string, timeoutMs: number): Promise<Response> => {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
};

export const openHelpDocInNewTab = async (title = "Help"): Promise<void> => {
  const helpWindow = window.open("", "_blank");
  if (!helpWindow) {
    return;
  }

  const helpUrls = buildHelpContentUrls();
  const cachedBodyHtml = readHelpCache();
  if (cachedBodyHtml) {
    writeHelpWindow(helpWindow, buildHtmlPage(cachedBodyHtml, title));
  } else {
    writeHelpWindow(
      helpWindow,
      buildHtmlPage("<p>Loading help...</p>", title),
    );
  }

  try {
    let bodyHtml: string | null = null;

    for (const helpUrl of helpUrls) {
      try {
        const response = await fetchWithTimeout(helpUrl, 8000);
        if (!response.ok) {
          continue;
        }
        const responseText = await response.text();
        if (looksLikeAppShellHtml(responseText)) {
          continue;
        }
        bodyHtml = responseText;
        break;
      } catch {
        // URL failed (timeout, network error, CORS) — try the next one.
      }
    }

    if (!bodyHtml) {
      throw new Error("Failed to load help content");
    }

    const html = buildHtmlPage(bodyHtml, title);

    try {
      writeHelpWindow(helpWindow, html);
      if (bodyHtml) {
        writeHelpCache(bodyHtml);
      }
    } catch (writeError) {
      console.error("Help doc rendering failed:", writeError);
      if (!cachedBodyHtml) {
        writeHelpWindow(helpWindow, buildErrorPage("Unable to render help content.", title));
      }
    }
  } catch (error) {
    console.error("Help doc conversion failed:", error);
    if (cachedBodyHtml) {
      writeHelpWindow(helpWindow, buildHtmlPage(cachedBodyHtml, title));
    } else {
      writeHelpWindow(helpWindow, buildErrorPage("Help content could not be loaded.", title));
    }
  }
};
